import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import { articles, categories } from '@/mocks/articles';

const websiteUrl = 'https://leyusports.com';

function getCategoryLabel(slug: string): string {
  const cat = categories.find(c => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';

  const filteredArticles = useMemo(() => {
    if (currentSlug === 'all') return articles;
    return articles.filter(a => a.categorySlug === currentSlug);
  }, [currentSlug]);

  const categoryLabel = getCategoryLabel(currentSlug);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryLabel} - 乐鱼体育资讯中心`,
    description: `乐鱼体育资讯中心${categoryLabel}频道，提供最新${categoryLabel}新闻、赛事动态、运动员资讯及赛程安排。`,
    url: `${websiteUrl}/category/${currentSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '乐鱼体育资讯中心',
      url: websiteUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: websiteUrl },
      { '@type': 'ListItem', position: 2, name: categoryLabel },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${categoryLabel} - 乐鱼体育资讯中心 | 最新${categoryLabel}新闻与赛事动态`}
        description={`乐鱼体育资讯中心${categoryLabel}频道，提供最新${categoryLabel}新闻、赛事动态、运动员资讯及赛程安排，让您第一时间掌握${categoryLabel}赛事资讯。`}
        keywords={`乐鱼体育${categoryLabel},${categoryLabel}新闻,${categoryLabel}赛事,乐鱼体育资讯中心,体育赛事资讯`}
        canonicalUrl={`${websiteUrl}/category/${currentSlug}`}
        schemaJson={{ '@context': 'https://schema.org', '@graph': [categorySchema, breadcrumbSchema] }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryLabel },
            ]}
          />

          <div className="mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">{categoryLabel}</h1>
            <p className="text-foreground-500 mt-2">
              共 {filteredArticles.length} 篇文章
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
              {filteredArticles.map((article) => (
                <NewsCard key={article.id} {...article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                <i className="ri-file-list-3-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}